import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const HealthTechAI = () => {
  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>HealthTech AI Solutions</h1>
          <p style={styles.heroSubtitle}>
            Revolutionizing healthcare with AI-powered diagnostics, accelerated drug discovery, and intelligent patient monitoring.
          </p>
        </div>
        <div style={styles.medicalVisualization}>
          <div style={styles.brainScan}></div>
          <div style={styles.dnaStrand}></div>
          <div style={styles.heartMonitor}></div>
        </div>
      </section>

      {/* Solutions Section */}
      <section style={styles.solutions}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>TECHNOLOGY</span>
          <h2 style={styles.sectionTitle}>AI Healthcare Innovations</h2>
          <p style={styles.sectionDescription}>
            Our cutting-edge AI solutions are transforming medical diagnosis, treatment development, and patient care.
          </p>
        </div>

        <div style={styles.solutionGrid}>
          <div style={styles.solutionCard}>
            <div style={styles.solutionIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="4"></circle>
                <line x1="4.93" y1="4.93" x2="9.17" y2="9.17"></line>
                <line x1="14.83" y1="14.83" x2="19.07" y2="19.07"></line>
                <line x1="14.83" y1="9.17" x2="19.07" y2="4.93"></line>
                <line x1="4.93" y1="19.07" x2="9.17" y2="14.83"></line>
              </svg>
            </div>
            <h3 style={styles.solutionTitle}>Diagnostic Imaging Analysis</h3>
            <p style={styles.solutionText}>
              AI-powered interpretation of medical images with 98% accuracy for early disease detection.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>CT/MRI/X-ray analysis</li>
              <li style={styles.featureListItem}>Tumor detection and tracking</li>
              <li style={styles.featureListItem}>Automated anomaly detection</li>
              <li style={styles.featureListItem}>Radiologist decision support</li>
            </ul>
          </div>

          <div style={styles.solutionCard}>
            <div style={styles.solutionIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"></path>
                <path d="M8.5 8.5v.01"></path>
                <path d="M16 15.5v.01"></path>
                <path d="M12 12v.01"></path>
                <path d="M11 17v.01"></path>
                <path d="M7 14v.01"></path>
              </svg>
            </div>
            <h3 style={styles.solutionTitle}>Drug Discovery Platform</h3>
            <p style={styles.solutionText}>
              Accelerated pharmaceutical research using AI molecular modeling and compound screening.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>Virtual compound screening</li>
              <li style={styles.featureListItem}>Toxicity prediction</li>
              <li style={styles.featureListItem}>Protein-ligand interaction</li>
              <li style={styles.featureListItem}>Clinical trial optimization</li>
            </ul>
          </div>

          <div style={styles.solutionCard}>
            <div style={styles.solutionIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                <path d="M12 7v4"></path>
                <path d="M10 9h4"></path>
              </svg>
            </div>
            <h3 style={styles.solutionTitle}>Patient Monitoring Systems</h3>
            <p style={styles.solutionText}>
              Real-time health tracking with predictive analytics for early intervention.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>Vital sign analysis</li>
              <li style={styles.featureListItem}>Deterioration prediction</li>
              <li style={styles.featureListItem}>Remote patient monitoring</li>
              <li style={styles.featureListItem}>Personalized care plans</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Clinical Impact Section */}
      <section style={styles.clinicalImpact}>
        <div style={styles.impactContent}>
          <div style={styles.impactText}>
            <span style={styles.sectionTag}>CLINICAL IMPACT</span>
            <h2 style={styles.sectionTitle}>Transforming Patient Outcomes</h2>
            <p style={styles.impactDescription}>
              Our AI solutions are proven to enhance diagnostic accuracy, reduce time-to-treatment, and improve survival rates.
            </p>
            <div style={styles.impactStats}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>40%</div>
                <div style={styles.statLabel}>Faster Diagnosis</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>35%</div>
                <div style={styles.statLabel}>Higher Drug Discovery Success</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>28%</div>
                <div style={styles.statLabel}>Reduced Hospital Readmissions</div>
              </div>
            </div>
          </div>
          <div style={styles.impactVisual}>
            <div style={styles.medicalDashboard}>
              <div style={styles.dashboardHeader}>
                <div style={styles.dashboardTitle}>Patient Health Dashboard</div>
              </div>
              <div style={styles.dashboardContent}>
                <div style={styles.vitalsChart}></div>
                <div style={styles.alertsPanel}>
                  <div style={styles.alertItemCritical}>Critical Alert</div>
                  <div style={styles.alertItemWarning}>Warning</div>
                  <div style={styles.alertItemNormal}>Normal</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section style={styles.caseStudies}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>CASE STUDIES</span>
          <h2 style={styles.sectionTitle}>Proven Healthcare Results</h2>
        </div>

        <div style={styles.studyGrid}>
          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>Oncology Center</h3>
            </div>
            <p style={styles.studyText}>
              Reduced false negatives in cancer detection by 45% using AI-powered radiology analysis.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>45%</span>
                <span style={styles.statLabel}>Fewer False Negatives</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>2.5x</span>
                <span style={styles.statLabel}>Faster Analysis</span>
              </div>
            </div>
          </div>

          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>Pharmaceutical Company</h3>
            </div>
            <p style={styles.studyText}>
              Identified 3 promising drug candidates in 6 months compared to typical 18-month timeline.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>3x</span>
                <span style={styles.statLabel}>Faster Discovery</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>$12M</span>
                <span style={styles.statLabel}>Cost Savings</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ModernCTA />
    </div>
  );
};

const styles = {
  container: {
    marginTop: '110px',
    color: '#ffffff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    lineHeight: 1.6,
    overflowX: 'hidden',
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      textAlign: 'left',
      justifyContent: 'space-between',
    },
  },
  heroContent: {
    maxWidth: '600px',
    marginBottom: '40px',
    '@media (min-width: 768px)': {
      marginBottom: 0,
    },
  },
  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '20px',
    background: 'linear-gradient(90deg, #ffffff 0%, #cbd5e1 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '@media (min-width: 768px)': {
      fontSize: '3.5rem',
    },
  },
  heroSubtitle: {
    fontSize: '1.1rem',
    color: '#94a3b8',
    marginBottom: '30px',
    maxWidth: '100%',
    '@media (min-width: 768px)': {
      fontSize: '1.25rem',
      maxWidth: '500px',
    },
  },
  medicalVisualization: {
    position: 'relative',
    width: '300px',
    height: '250px',
    '@media (min-width: 768px)': {
      width: '400px',
      height: '300px',
    },
  },
  brainScan: {
    position: 'absolute',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
    border: '1px solid rgba(99, 102, 241, 0.5)',
    top: '20px',
    right: '30px',
    '@media (min-width: 768px)': {
      width: '150px',
      height: '150px',
      right: '50px',
    },
  },
  dnaStrand: {
    position: 'absolute',
    width: '80px',
    height: '160px',
    background: 'linear-gradient(90deg, transparent 45%, rgba(16, 185, 129, 0.3) 45%, rgba(16, 185, 129, 0.3) 55%, transparent 55%)',
    backgroundSize: '15px 100%',
    bottom: '0',
    right: '0',
    '@media (min-width: 768px)': {
      width: '100px',
      height: '200px',
    },
  },
  heartMonitor: {
    position: 'absolute',
    width: '150px',
    height: '60px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    bottom: '20px',
    left: '0',
    '@media (min-width: 768px)': {
      width: '180px',
      height: '80px',
    },
  },
  solutions: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  sectionHeader: {
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 40px',
    '@media (min-width: 768px)': {
      marginBottom: '60px',
    },
  },
  sectionTag: {
    display: 'inline-block',
    color: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: '16px',
    lineHeight: 1.3,
    '@media (min-width: 768px)': {
      fontSize: '2.5rem',
    },
  },
  sectionDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
    },
  },
  solutionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 640px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
    },
  },
  solutionCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px 32px',
    },
  },
  solutionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    color: '#3b82f6',
    '@media (min-width: 768px)': {
      width: '56px',
      height: '56px',
      marginBottom: '24px',
    },
  },
  solutionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '12px',
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
      marginBottom: '16px',
    },
  },
  solutionText: {
    color: '#94a3b8',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '24px',
    },
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureListItem: {
    marginBottom: '10px',
    color: '#e2e8f0',
    fontSize: '0.95rem',
    position: 'relative',
    paddingLeft: '24px',
    '@media (min-width: 768px)': {
      marginBottom: '12px',
      fontSize: '1rem',
    },
  },
  clinicalImpact: {
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  impactContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '40px',
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '60px',
    },
  },
  impactText: {
    flex: 1,
    width: '100%',
    '@media (min-width: 1024px)': {
      width: 'auto',
    },
  },
  impactDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    margin: '20px 0 30px',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
      margin: '24px 0 32px',
    },
  },
  impactStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: '16px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  statCard: {
    background: 'rgba(15, 23, 42, 0.5)',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '20px',
    },
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#3b82f6',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '2rem',
    },
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    marginTop: '6px',
    '@media (min-width: 768px)': {
      fontSize: '0.875rem',
      marginTop: '8px',
    },
  },
  impactVisual: {
    flex: 1,
    width: '100%',
    '@media (min-width: 1024px)': {
      width: 'auto',
    },
  },
  medicalDashboard: {
    background: '#0f172a',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  },
  dashboardHeader: {
    height: '40px',
    background: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    '@media (min-width: 768px)': {
      height: '50px',
      padding: '0 16px',
    },
  },
  dashboardTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    '@media (min-width: 768px)': {
      fontSize: '1rem',
    },
  },
  dashboardContent: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      padding: '24px',
      gap: '24px',
    },
  },
  vitalsChart: {
    height: '120px',
    background: 'rgba(59, 130, 246, 0.05)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: '8px',
    '@media (min-width: 768px)': {
      height: '150px',
    },
  },
  alertsPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '@media (min-width: 768px)': {
      gap: '8px',
    },
  },
  alertItemCritical: {
    background: 'rgba(239, 68, 68, 0.1)',
    borderLeft: '4px solid #ef4444',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    '@media (min-width: 768px)': {
      padding: '12px',
      fontSize: '0.875rem',
    },
  },
  alertItemWarning: {
    background: 'rgba(234, 179, 8, 0.1)',
    borderLeft: '4px solid #eab308',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    '@media (min-width: 768px)': {
      padding: '12px',
      fontSize: '0.875rem',
    },
  },
  alertItemNormal: {
    background: 'rgba(16, 185, 129, 0.1)',
    borderLeft: '4px solid #10b981',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    '@media (min-width: 768px)': {
      padding: '12px',
      fontSize: '0.875rem',
    },
  },
  caseStudies: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  studyGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '32px',
    },
  },
  studyCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px',
    },
  },
  studyHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      marginBottom: '24px',
    },
  },
  studyLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    marginRight: '12px',
    '@media (min-width: 768px)': {
      width: '48px',
      height: '48px',
      marginRight: '16px',
    },
  },
  studyTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
    },
  },
  studyText: {
    color: '#94a3b8',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginBottom: '24px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '32px',
    },
  },
  studyStats: {
    display: 'flex',
    gap: '16px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#3b82f6',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '2rem',
    },
  },
  statLabel: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    '@media (min-width: 768px)': {
      fontSize: '0.875rem',
    },
  },
};

export default HealthTechAI;